import { AnyGameState } from "@/app/lib/game-state/game-types";
import WordModule from "@/app/lib/game-state/word-module";
import LetterModule from "@/app/lib/game-state/letter-module";

export default class Nav {

    currentWordIndex: number;
    currentLetterPosition: number;
    gameState: AnyGameState
    currentPosition: {
        wordIndex: number,
        letterPosition: number
    }

    constructor(
        currentWordIndex: number,
        currentLetterPosition: number,
        gameState: AnyGameState
    )
    {
        this.currentWordIndex = currentWordIndex;
        this.currentLetterPosition = currentLetterPosition;
        this.gameState = gameState;
        this.currentPosition = {
            wordIndex: currentWordIndex,
            letterPosition: currentLetterPosition
        }
    }

    // SIMPLE NAV
    // ----------------------------------------------------
    findUp(): {wordIndex: number, letterPosition: number} {
        // check that we can
        if (this.currentWordIndex <= 0)
            return this.currentPosition;

        const targetWordModule: WordModule = 
            this.gameState.words[this.currentWordIndex - 1];

        if (targetWordModule.blockSimpleNav())
            return this.currentPosition;

        return {wordIndex: this.currentWordIndex - 1, letterPosition: this.currentLetterPosition};
    }

    findDown(): {wordIndex: number, letterPosition: number} {
        // check that we can
        if (this.currentWordIndex >= this.gameState.words.length - 1)
            return this.currentPosition;

        const targetWordModule: WordModule = 
            this.gameState.words[this.currentWordIndex + 1];

        if (targetWordModule.blockSimpleNav())
            return this.currentPosition;
        
        return {wordIndex: this.currentWordIndex + 1, letterPosition: this.currentLetterPosition};
    }

    findLeft(): {wordIndex: number, letterPosition: number} {
        // check that we can
        if (this.currentLetterPosition <= 0)
            return this.currentPosition;
        
        return {wordIndex: this.currentWordIndex, letterPosition: this.currentLetterPosition - 1};
    }
    
    findRight(): {wordIndex: number, letterPosition: number} {
        // check that we can
        const currentWord = this.gameState.words[this.currentWordIndex];
        if (this.currentLetterPosition >= currentWord.length - 1)
            return this.currentPosition;
        
        return {wordIndex: this.currentWordIndex, letterPosition: this.currentLetterPosition + 1};
    }

    // NAV BY ENTRY SLOT
    //--------------------------------------------------------------------------------

    findNextEntrySlot(
        wrapToNextWord: boolean
    ): { 
        wordIndex: number,
        letterPosition: number 
    } {
        // if we won, don't go on an infinite loop
        if (this.gameState.victorious) {
            return this.currentPosition;
        }

        // readability
        const currentWord: WordModule = this.gameState.words[this.currentWordIndex];

        // first, does the current word have another entry slot
        for (let i = this.currentLetterPosition + 1; i < currentWord.length; i++) {
            // hacking this in as a bugfix
            // we skip this word if it blocks nav
            if (currentWord.blockComplexNav())
                break;
            // if we find another entry slot, we're done
            if (currentWord.letters[i].type === 'entry') {
                return {
                    wordIndex: this.currentWordIndex, 
                    letterPosition: i
                };
            }
        }

        // down here, we've exhausted possibilities in this word
        // if we're not wrapping, return current position
        if (!wrapToNextWord) {
            return this.currentPosition;
        }

        // otherwise, we move to the next unblocked word
        let nextWordIndex: number = this.currentWordIndex;
        let nextWord: WordModule;

        // loop through till we find an unblocked word
        do {
            nextWordIndex = (nextWordIndex + 1) % this.gameState.words.length;
            nextWord = this.gameState.words[nextWordIndex];
        } while (nextWord.blockComplexNav());

        // find the first entry slot in the new word
        let nextEntrySlot = 0;
        for (let i = 0; i < nextWord.length; i++) {
            if (nextWord.letters[i].type === 'entry') {
                nextEntrySlot = i;
                break;
            }
        }
        return {
            wordIndex: nextWordIndex, 
            letterPosition: nextEntrySlot
        };
    }

    findPreviousEntrySlot(
        wrapToNextWord: boolean
    ): {
        wordIndex: number, 
        letterPosition: number 
    } {
        // if we won, don't go on an infinite loop
        if (this.gameState.victorious) {
            return this.currentPosition;
        }

        // readability
        const currentWord: WordModule = this.gameState.words[this.currentWordIndex];

        // first, does the current word have a prior entry slot?
        for (let i = this.currentLetterPosition - 1; i >= 0; i--) {
            // hacking this in as a bugfix
            // we skip this word if it's blocked
            if (currentWord.blockComplexNav())
                break;
            // if we find one here, we're done
            if (currentWord.letters[i].type === 'entry') {
                return {wordIndex: this.currentWordIndex, letterPosition: i}
            }
        }

        // down here, we've exhausted possibilities with this word
        // if we're not wrapping, return current position
        if (!wrapToNextWord) {
            return this.currentPosition;
        }

        // otherwise, we move to the prior unblocked word
        let prevWordIndex: number = this.currentWordIndex;
        let prevWord: WordModule;

        // loop through till we find an unblocked word
        do {
            prevWordIndex = (prevWordIndex - 1 + this.gameState.words.length) % this.gameState.words.length;
            prevWord = this.gameState.words[prevWordIndex];
        } while (prevWord.blockComplexNav());

        // find the last entry slot in the new word
        let prevEntrySlot = prevWord.length - 1;
        for (let i = prevWord.length - 1; i >= 0; i--) {
            if (prevWord.letters[i].type === 'entry') {
                prevEntrySlot = i;
                break;
            }
        }
        return {
            wordIndex: prevWordIndex, 
            letterPosition: prevEntrySlot
        };
    }

    // finds the first entry slot in a given word
    findFirstEntrySlot() : {wordIndex: number, letterPosition: number} {

        const wordModule: WordModule = this.gameState.words[this.currentWordIndex];

        // a solved word simply returns the first slot of any kind
        if (wordModule.solved) {
            return { wordIndex: this.currentWordIndex, letterPosition: 0};
        }

        for (let i = 0; i < wordModule.length; i++) {
            if (
                wordModule.letters[i]?.type === 'entry'
                && wordModule.blockComplexNav() === false
            ) {
                return { wordIndex: this.currentWordIndex, letterPosition: i};
            }
        }
        return this.currentPosition;
    }

    // finds the last entry slot in a given word
    findLastEntrySlot() : {wordIndex: number, letterPosition: number} {

        const wordModule: WordModule = this.gameState.words[this.currentWordIndex];

        // a solved word returns the last slot of any kind
        if (wordModule.solved) {
            return { wordIndex: this.currentWordIndex, letterPosition: wordModule.length - 1};
        }

        // start from end of word
        for (let i = wordModule.length - 1; i >= 0; i--) {
            if (
                wordModule.letters[i]?.type === 'entry'
                && wordModule.blockComplexNav() === false
            ) {
                return { wordIndex: this.currentWordIndex, letterPosition: i};
            }
        }
        return this.currentPosition;
    }

    findEarliestEmptySlot() : {wordIndex: number, letterPosition: number} | null {
        if (this.gameState.victorious) 
            return null;

        for (const wordModule of this.gameState.words) {
            if (wordModule.blockComplexNav()) {
                continue;
            }
            for (const letterModule of wordModule.letters) {
                if (
                    letterModule.type === 'entry'
                    && letterModule.character === ''
                ) {
                    return {wordIndex: wordModule.index, letterPosition: letterModule.position}
                }
            }
        }

        return null;
    }
}